import { Feature, isEnabled } from '../shared/utils/featureToggle';
import { StoreEnum } from './storeCode';

export enum LocaleEnum {
  /**
   * English with USA store
   */
  enUS = 'en-us',
  /**
   * APAC with USA store
   */
  apacUS = 'apac-us',
  /**
   * English with AU store (multicurrency for APAC)
   */
  enAU = 'en-au',
  /**
   * English with NZ store (multicurrency for APAC)
   */
  enNZ = 'en-nz',
  /**
   * English with United Kingdom store
   */
  enUK = 'en-uk',
  /**
   * English with Europe store
   */
  enEU = 'en-eu',
  /**
   * German with Europe store
   */
  deEU = 'de-eu',
  /**
   * Swedish with Europe store
   */
  seEU = 'se-eu',
  /**
   * Dutch with Europe store
   */
  nlEU = 'nl-eu',
  /**
   * Spanish with Europe store
   */
  esEU = 'es-eu',
  /**
   * French with Europe store
   */
  frEU = 'fr-eu',
  /**
   * Japanese with Japan store
   */
  enJP = 'en-jp',
  jaJP = 'ja-jp',

  enDev = 'en-dev',
  enQa = 'en-qa',
  enAud = 'en-aud',
  enNzd = 'en-nzd',

  deQa = 'de-qa',
  enEud = 'en-eud',
  deEud = 'de-eud',
  enUkd = 'en-ukd'
}

export const DefaultLocale = LocaleEnum.enUS;

export enum ShortLocaleEnum {
  en = 'en',
  de = 'de',
  se = 'se',
  nl = 'nl',
  es = 'es',
  fr = 'fr',
  apac = 'apac'
}

// Handle JP separately since it is not a directly supported locale
// Prevents unnecessary sitewide handling for ShortLocaleEnum
export const JPShortLocale = 'jp' as ShortLocaleEnum;

export const LanguagesOptions = [
  {
    label: 'Deutsch',
    value: ShortLocaleEnum.de
  },
  {
    label: 'English',
    value: ShortLocaleEnum.en
  },
  {
    label: 'Swedish',
    value: ShortLocaleEnum.se
  },
  {
    label: 'Dutch',
    value: ShortLocaleEnum.nl
  },
  {
    label: 'Spanish',
    value: ShortLocaleEnum.es
  },
  {
    label: 'French',
    value: ShortLocaleEnum.fr
  },
  {
    label: 'Asia Pacific',
    value: ShortLocaleEnum.apac
  },
  {
    label: 'Japanese',
    value: JPShortLocale
  }
];

export const DEFAULT_LANGUAGE = ShortLocaleEnum.en;

/**
 * Short languages array ["en", "de", ...]
 */
export const shortLanguages = Object.values(ShortLocaleEnum);

export const nonDefaultShortLanguages = shortLanguages.filter((lang) => lang !== DEFAULT_LANGUAGE);

/**
 * Return store for current locale
 */
export const LocaleStore: Record<LocaleEnum, StoreEnum> = {
  /**
   * United States store
   */
  [LocaleEnum.enUS]: StoreEnum.us,
  [LocaleEnum.apacUS]: StoreEnum.us,
  [LocaleEnum.enAU]: StoreEnum.au,
  [LocaleEnum.enNZ]: StoreEnum.nz,
  /**
   * United Kingdom store
   */
  [LocaleEnum.enUK]: StoreEnum.uk,
  /**
   * Europe store
   */
  [LocaleEnum.enEU]: StoreEnum.eu,
  /**
   * Europe store
   */
  [LocaleEnum.deEU]: StoreEnum.eu,
  [LocaleEnum.seEU]: StoreEnum.eu,
  [LocaleEnum.nlEU]: StoreEnum.eu,
  [LocaleEnum.esEU]: StoreEnum.eu,
  [LocaleEnum.frEU]: StoreEnum.eu,
  /**
   * Japan store
   */
  [LocaleEnum.enJP]: StoreEnum.jp,
  [LocaleEnum.jaJP]: StoreEnum.jp,

  [LocaleEnum.enDev]: StoreEnum.dev,
  [LocaleEnum.enQa]: StoreEnum.qa,
  [LocaleEnum.deQa]: StoreEnum.qa,
  [LocaleEnum.enAud]: StoreEnum.aud,
  [LocaleEnum.enNzd]: StoreEnum.nzd,
  [LocaleEnum.enEud]: StoreEnum.eud,
  [LocaleEnum.deEud]: StoreEnum.eud,
  [LocaleEnum.enUkd]: StoreEnum.ukd
};

export const LOCALE_TO_SHOPIFY_COUNTRY_CODE: Readonly<Record<LocaleEnum, string>> = {
  [LocaleEnum.enUS]: 'US',
  [LocaleEnum.apacUS]: 'US',
  [LocaleEnum.enAU]: 'AU',
  [LocaleEnum.enNZ]: 'NZ',
  [LocaleEnum.enUK]: 'GB',
  [LocaleEnum.enEU]: 'ES', // since EU isn't a country code
  [LocaleEnum.deEU]: 'DE',
  [LocaleEnum.seEU]: 'SE',
  [LocaleEnum.nlEU]: 'NL',
  [LocaleEnum.esEU]: 'ES',
  [LocaleEnum.frEU]: 'FR',
  [LocaleEnum.enJP]: 'JP',
  [LocaleEnum.jaJP]: 'JP',
  [LocaleEnum.enDev]: 'US',
  [LocaleEnum.enQa]: 'US',
  [LocaleEnum.deQa]: 'DE',
  [LocaleEnum.enAud]: 'AU',
  [LocaleEnum.enNzd]: 'NZ',
  [LocaleEnum.enEud]: 'ES', // since EU isn't a country code
  [LocaleEnum.deEud]: 'DE',
  [LocaleEnum.enUkd]: 'GB'
};

export type ShopifyCountryCode =
  (typeof LOCALE_TO_SHOPIFY_COUNTRY_CODE)[keyof typeof LOCALE_TO_SHOPIFY_COUNTRY_CODE];

export const ISOLocale = {
  [LocaleEnum.enUS]: 'en_US',
  [LocaleEnum.apacUS]: 'en_US',
  [LocaleEnum.enAU]: 'en_US',
  [LocaleEnum.enNZ]: 'en_US',
  [LocaleEnum.enUK]: 'en_GB',
  [LocaleEnum.enEU]: 'en_US',
  [LocaleEnum.deEU]: 'de_DE',
  [LocaleEnum.enJP]: 'en_JP',
  [LocaleEnum.jaJP]: 'ja_JP',
  [LocaleEnum.enDev]: 'en_US',
  [LocaleEnum.enQa]: 'en_US',
  [LocaleEnum.deQa]: 'de_DE',
  [LocaleEnum.enAud]: 'en_US',
  [LocaleEnum.enNzd]: 'en_US',
  [LocaleEnum.enEud]: 'en_US',
  [LocaleEnum.deEud]: 'de_DE',
  [LocaleEnum.enUkd]: 'en_GB',
  [LocaleEnum.seEU]: 'sv_SE',
  [LocaleEnum.nlEU]: 'nl_NL',
  [LocaleEnum.esEU]: 'es_ES',
  [LocaleEnum.frEU]: 'fr_FR'
} as const satisfies Record<LocaleEnum, string>;

/**
 * A mapping of our weird locales to BCP47 languages.
 *
 * Check the languages with: https://r12a.github.io/app-subtags/
 *
 * Note that you only need the region code if it contributes meaningfully to the
 * language. Mostly just en-GB right now, since they spell and grammar things in
 * fun ways.
 */
export const BCP47Language = {
  [LocaleEnum.enUS]: 'en',
  [LocaleEnum.apacUS]: 'en',
  [LocaleEnum.enAU]: 'en-AU',
  [LocaleEnum.enNZ]: 'en-NZ',
  [LocaleEnum.enUK]: 'en-GB',
  [LocaleEnum.enEU]: 'en',
  [LocaleEnum.deEU]: 'de',
  [LocaleEnum.enJP]: 'en',
  [LocaleEnum.jaJP]: 'ja',
  [LocaleEnum.seEU]: 'sv',
  [LocaleEnum.nlEU]: 'nl',
  [LocaleEnum.esEU]: 'es',
  [LocaleEnum.frEU]: 'fr',

  [LocaleEnum.enQa]: 'en',
  [LocaleEnum.deQa]: 'de',
  [LocaleEnum.enAud]: 'en-AU',
  [LocaleEnum.enNzd]: 'en-NZ',

  [LocaleEnum.enDev]: 'en',
  [LocaleEnum.enEud]: 'en',
  [LocaleEnum.deEud]: 'de',
  [LocaleEnum.enUkd]: 'en-GB',

  [ShortLocaleEnum.en]: 'en',
  [ShortLocaleEnum.de]: 'de',
  [ShortLocaleEnum.se]: 'sv',
  [ShortLocaleEnum.nl]: 'nl',
  [ShortLocaleEnum.es]: 'es',
  [ShortLocaleEnum.fr]: 'fr',
  [ShortLocaleEnum.apac]: 'en'
} as const satisfies Record<LocaleEnum | ShortLocaleEnum, string>;

export const ShopifyLocaleCode = {
  [LocaleEnum.enUS]: '',
  [LocaleEnum.apacUS]: '',
  [LocaleEnum.enAU]: '',
  [LocaleEnum.enNZ]: '',
  [LocaleEnum.enUK]: '',
  [LocaleEnum.enEU]: '',
  [LocaleEnum.enJP]: '',
  [LocaleEnum.jaJP]: '',
  [LocaleEnum.deEU]: 'de',
  [LocaleEnum.seEU]: 'sv',
  [LocaleEnum.nlEU]: 'nl',
  [LocaleEnum.esEU]: 'es',
  [LocaleEnum.frEU]: 'fr',
  [LocaleEnum.enDev]: '',
  [LocaleEnum.enQa]: '',
  [LocaleEnum.deQa]: 'de',
  [LocaleEnum.enAud]: '',
  [LocaleEnum.enNzd]: '',
  [LocaleEnum.enEud]: '',
  [LocaleEnum.deEud]: 'de',
  [LocaleEnum.enUkd]: ''
} as const;

export type ShopifyLocaleCodeType = typeof ShopifyLocaleCode;

export enum ISOLocalesEnum {
  'en-us' = 'en-us',
  'apac-us' = 'apac-us',
  'en-au' = 'en-au',
  'en-nz' = 'en-nz',
  'en-uk' = 'en-uk',
  'en-eu' = 'en-eu',
  'en-jp' = 'en-jp',
  'ja-jp' = 'ja-jp',
  'de-eu' = 'de-eu',
  'de-de' = 'de-de', // qa-only
  'sv-se' = 'sv-se',
  'nl-eu' = 'nl-eu',
  'es-eu' = 'es-eu',
  'fr-eu' = 'fr-eu'
}

interface ConfigItem {
  language: ShortLocaleEnum;
  store: StoreEnum;
  isoLanguage: ISOLocalesEnum;
  isQa: boolean;
}

export interface FullLocale extends ConfigItem {
  locale: LocaleEnum;
}

/**
 * Chain between language, locale and stores
 */
export const fullLocales: FullLocale[] = (() => {
  const config: Record<LocaleEnum, ConfigItem> = {
    [LocaleEnum.enUS]: {
      language: ShortLocaleEnum.en,
      isoLanguage: ISOLocalesEnum['en-us'],
      store: StoreEnum.us,
      isQa: false
    },
    [LocaleEnum.apacUS]: {
      language: ShortLocaleEnum.apac,
      isoLanguage: ISOLocalesEnum['apac-us'],
      store: StoreEnum.us,
      isQa: false
    },
    [LocaleEnum.enAU]: {
      language: ShortLocaleEnum.en,
      isoLanguage: ISOLocalesEnum['en-au'],
      store: StoreEnum.au,
      isQa: false
    },
    [LocaleEnum.enNZ]: {
      language: ShortLocaleEnum.en,
      isoLanguage: ISOLocalesEnum['en-nz'],
      store: StoreEnum.nz,
      isQa: false
    },
    [LocaleEnum.enUK]: {
      language: ShortLocaleEnum.en,
      isoLanguage: ISOLocalesEnum['en-us'],
      store: StoreEnum.uk,
      isQa: false
    },
    [LocaleEnum.enJP]: {
      language: ShortLocaleEnum.en,
      isoLanguage: ISOLocalesEnum['en-us'],
      store: StoreEnum.jp,
      isQa: false
    },
    [LocaleEnum.jaJP]: {
      language: JPShortLocale,
      isoLanguage: ISOLocalesEnum['ja-jp'],
      store: StoreEnum.jp,
      isQa: false
    },
    [LocaleEnum.enEU]: {
      language: ShortLocaleEnum.en,
      isoLanguage: ISOLocalesEnum['en-us'],
      store: StoreEnum.eu,
      isQa: false
    },
    [LocaleEnum.deEU]: {
      language: ShortLocaleEnum.de,
      isoLanguage: ISOLocalesEnum['de-eu'],
      store: StoreEnum.eu,
      isQa: false
    },
    [LocaleEnum.enDev]: {
      language: ShortLocaleEnum.en,
      isoLanguage: ISOLocalesEnum['en-us'],
      store: StoreEnum.dev,
      isQa: true
    },
    [LocaleEnum.enQa]: {
      language: ShortLocaleEnum.en,
      isoLanguage: ISOLocalesEnum['en-us'],
      store: StoreEnum.qa,
      isQa: true
    },
    [LocaleEnum.deQa]: {
      language: ShortLocaleEnum.de,
      isoLanguage: ISOLocalesEnum['de-de'],
      store: StoreEnum.qa,
      isQa: true
    },
    [LocaleEnum.enAud]: {
      language: ShortLocaleEnum.en,
      isoLanguage: ISOLocalesEnum['en-us'],
      store: StoreEnum.aud,
      isQa: true
    },
    [LocaleEnum.enNzd]: {
      language: ShortLocaleEnum.en,
      isoLanguage: ISOLocalesEnum['en-us'],
      store: StoreEnum.nzd,
      isQa: true
    },
    [LocaleEnum.enEud]: {
      language: ShortLocaleEnum.en,
      isoLanguage: ISOLocalesEnum['en-us'],
      store: StoreEnum.eud,
      isQa: true
    },
    [LocaleEnum.deEud]: {
      language: ShortLocaleEnum.de,
      isoLanguage: ISOLocalesEnum['de-de'],
      store: StoreEnum.eud,
      isQa: true
    },
    [LocaleEnum.enUkd]: {
      language: ShortLocaleEnum.en,
      isoLanguage: ISOLocalesEnum['en-uk'],
      store: StoreEnum.ukd,
      isQa: true
    },
    [LocaleEnum.seEU]: {
      language: ShortLocaleEnum.se,
      isoLanguage: ISOLocalesEnum['sv-se'],
      store: StoreEnum.eu,
      isQa: false
    },
    [LocaleEnum.nlEU]: {
      language: ShortLocaleEnum.nl,
      isoLanguage: ISOLocalesEnum['nl-eu'],
      store: StoreEnum.eu,
      isQa: false
    },
    [LocaleEnum.esEU]: {
      language: ShortLocaleEnum.es,
      isoLanguage: ISOLocalesEnum['es-eu'],
      store: StoreEnum.eu,
      isQa: false
    },
    [LocaleEnum.frEU]: {
      language: ShortLocaleEnum.fr,
      isoLanguage: ISOLocalesEnum['fr-eu'],
      store: StoreEnum.eu,
      isQa: false
    }
  };

  return Object.entries(config)
    .map(([locale, configItem]) => ({ ...configItem, locale: locale as LocaleEnum }))
    .filter((item) => {
      if (isEnabled(Feature.storeQA)) {
        return true;
      }
      return !item.isQa;
    });
})();

/**
 * Languages array ["en-us", "en-uk", "de-eu", ...]
 */
export const languages = fullLocales.map((item) => item.locale);

export const isLanguageLocale = (value: unknown): value is (typeof languages)[number] => {
  return !!value && typeof value === 'string' && languages.includes(value as any);
};

export type LanguageValue =
  | 'english'
  | 'german'
  | 'swedish'
  | 'dutch'
  | 'spanish'
  | 'french'
  | 'apac';

const ShortLocaleToLanguageValue = {
  [ShortLocaleEnum.en]: 'english',
  [ShortLocaleEnum.de]: 'german',
  [ShortLocaleEnum.se]: 'swedish',
  [ShortLocaleEnum.nl]: 'dutch',
  [ShortLocaleEnum.es]: 'spanish',
  [ShortLocaleEnum.fr]: 'french',
  [ShortLocaleEnum.apac]: 'apac'
} as const satisfies Record<ShortLocaleEnum, string>;

export function localeEnumToLanguageName(locale: LocaleEnum | null): LanguageValue | null {
  if (!locale) {
    return null;
  }

  const shortLocale = fullLocales.find((item) => item.locale === locale)?.language ?? null;
  if (!shortLocale) {
    return null;
  }

  return ShortLocaleToLanguageValue[shortLocale] ?? null;
}

/**
 * Old next locale without currency to new localeEnum with currency
 */
export const oldLocaleMap: Record<string, LocaleEnum> = {
  de: LocaleEnum.deEU,
  en: LocaleEnum.enUS
};
